<template>
  <!-- Cabeçalho da aba: QUEM produziu o horário daquela perna. Fica acima da
       evidência propriamente dita (folha, mapa/biometria ou anexos) porque o
       gestor primeiro pergunta "de onde veio esse horário?" e só depois "me
       mostra o arquivo". -->
  <div
    class="flex flex-col gap-2 rounded border border-primary30 bg-primary5 px-3 py-2 text-left text-xs"
    :data-testid="`station-source-origin-summary-${leg}`"
  >
    <dl class="m-0 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
      <div class="flex flex-col">
        <dt class="font-bold uppercase text-gray-500">Fonte</dt>
        <dd class="m-0" :data-testid="`station-source-origin-label-${leg}`">{{ sourceLabel }}</dd>
      </div>
      <div v-if="timeLabel" class="flex flex-col">
        <dt class="font-bold uppercase text-gray-500">Horário</dt>
        <dd class="m-0">{{ timeLabel }}</dd>
      </div>
      <div v-if="methodLabel" class="flex flex-col">
        <dt class="font-bold uppercase text-gray-500">Método</dt>
        <dd class="m-0">{{ methodLabel }}</dd>
      </div>
      <div v-if="rankLabel" class="flex flex-col">
        <dt class="font-bold uppercase text-gray-500">Ranking</dt>
        <dd class="m-0">{{ rankLabel }}</dd>
      </div>
    </dl>

    <p class="m-0 text-gray-600" :data-testid="`station-source-origin-explanation-${leg}`">
      {{ explanation }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
  EFFECTIVE_SOURCE_KIND_LABELS,
  EFFECTIVE_SOURCE_METHOD_PINNED,
  EffectiveSourceKind,
  effectiveSourceMethodLabel
} from '@/constants/ds-attendance.constants'
import type { StationLegKey, StationSourceOrigin } from '@/types/station-evidence.types'
import { formatHourMinuteInOffset } from '@/utils/date'

const props = withDefaults(
  defineProps<{
    leg: StationLegKey
    source: StationSourceOrigin
    /** Horário efetivo da perna (ISO). */
    at?: string | null
    timezone?: string
  }>(),
  { at: null, timezone: '-03:00' }
)

const sourceLabel = computed(
  () => props.source.label || EFFECTIVE_SOURCE_KIND_LABELS[props.source.kind] || props.source.kind
)

const timeLabel = computed(() =>
  props.at ? formatHourMinuteInOffset(props.at, props.timezone) : null
)

const methodLabel = computed(() => effectiveSourceMethodLabel(props.source.method))

// `99` é o código da F1 para "fora do ranking configurado na escala" — mostrar
// "99º" seria mentira aritmética; o texto diz o que o número significa. Perna fixada pela
// sobrescrita não venceu pela posição: dizer "1º no ranking" seria outra mentira.
const rankLabel = computed(() => {
  if (props.source.method === EFFECTIVE_SOURCE_METHOD_PINNED) {
    return 'Fixada pela sobrescrita — vale acima do ranking'
  }
  const rank = props.source.rank
  if (rank == null) return null
  return rank === 99 ? 'Fora do ranking da escala' : `${rank}º no ranking aplicável`
})

const EXPLANATIONS: Partial<Record<EffectiveSourceKind, string>> = {
  [EffectiveSourceKind.APP]:
    'Check registrado pelo médico no app, com geolocalização e, quando exigida, biometria.',
  [EffectiveSourceKind.EXTERNAL_APP]:
    'Check importado de um aplicativo externo integrado à escala.',
  [EffectiveSourceKind.SHEET]: 'Linha conciliada e aprovada na folha de presença desta escala.',
  [EffectiveSourceKind.ACTIVITY_RECORD]:
    'Equivalência aprovada em um Registro de Atividade desta vaga.',
  [EffectiveSourceKind.AUTO_PERMANENCE]:
    'Gerada automaticamente pela permanência entre plantões encadeados — não há anexo a exibir.',
  [EffectiveSourceKind.MANUAL_LEGACY]:
    'Check lançado à mão no painel antes da aposentadoria do fluxo manual. Fica no fim do ranking.',
  [EffectiveSourceKind.LEGACY]:
    'Horário herdado da base anterior ao ranking de fontes; não há origem detalhada a abrir.'
}

const explanation = computed(
  () => EXPLANATIONS[props.source.kind] ?? 'Origem sem detalhamento disponível para esta fonte.'
)
</script>
